import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { DocumentsData } from '@/features/ui/DocumentsData';

import { FormTextInput, FormSelect, StepControlPanel, Grid } from '@/shared/ui';

import { FormConstants } from '@/components/Forms/constants';

import { useERZDocumentTypes } from '@/modules/erz/helpers';

import { rmp } from '@/api/integrations/rmp';
import { queryKeys } from '@/api/queryKeys';
import { DocumentTypeCode } from '@/api/types';

import { missingPersonAllowedFileTypes, YesNoSelectOptions } from '../../lib/constants';
import { MissingPersonFormData } from '../../model/types';

export const PersonalFeatures: React.FC<StepProps> = ({ next, prev, currentRequestId, onStepDraftSave }) => {
  const { control } = useFormContext<MissingPersonFormData>();

  const { data: statureTypes = [] } = useQuery({
    queryKey: queryKeys.rmp.statureTypes(),
    queryFn: () =>
      rmp.dictionary.getPersonalStatureTypes().then((r) => r.data.map((t) => ({ id: t.id, label: t.name }))),
  });
  const { data: hairColors = [] } = useQuery({
    queryKey: queryKeys.rmp.hairColors(),
    queryFn: () => rmp.dictionary.getPersonalHairColors().then((r) => r.data.map((t) => ({ id: t.id, label: t.name }))),
  });
  const { data: eyeColors = [] } = useQuery({
    queryKey: queryKeys.rmp.eyeColors(),
    queryFn: () => rmp.dictionary.getPersonalEyeColors().then((r) => r.data.map((t) => ({ id: t.id, label: t.name }))),
  });
  const { data: bloodGroups = [] } = useQuery({
    queryKey: queryKeys.rmp.bloodGroups(),
    queryFn: () =>
      rmp.dictionary.getPersonalBloodGroups().then((r) => r.data.map((t) => ({ id: t.id, label: t.name }))),
  });

  const { data: additionalFilesDocs = [] } = useERZDocumentTypes({
    allowedDocumentTypeCodes: [DocumentTypeCode.MISSIN_PERSON_ADDITIONAL_PERSONAL_INFO],
  });
  const additionalFilesDocType = additionalFilesDocs[0];

  return (
    <>
      <FormBlock title="Особисті прикмети">
        <FormSelect name="missingPerson.person_stature" control={control} label="Статура" options={statureTypes} />

        <FormTextInput
          placeholder="Вкажіть зріст в см (наприклад: 178,5)"
          name="missingPerson.person_height"
          control={control}
          maxLength={6}
          label="Зріст"
          required
        />
        <FormSelect
          name="missingPerson.person_hair_color"
          control={control}
          label="Колір волосся"
          options={hairColors}
          required
        />
        <FormSelect
          name="missingPerson.person_eye_color"
          control={control}
          label="Колір очей"
          options={eyeColors}
          required
        />
        <FormSelect
          name="missingPerson.personal_baldness"
          control={control}
          label="Наявність залисин"
          options={[YesNoSelectOptions.YES, YesNoSelectOptions.NO]}
        />
        <FormTextInput
          name="missingPerson.personal_foot_size"
          control={control}
          label="Розмір ноги"
          maxLength={4}
          required
        />
        <FormSelect
          name="missingPerson.personal_blood_group"
          control={control}
          label="Група крові"
          options={bloodGroups}
          required
        />
        <FormTextInput
          label="Шрами"
          name="missingPerson.personal_scars"
          control={control}
          placeholder="Опишіть детально"
          maxLength={FormConstants.INPUT_MAX_LENGTH_100}
        />
        <FormTextInput
          label="Тату"
          name="missingPerson.personal_tattoo"
          control={control}
          placeholder="Опишіть детально"
          maxLength={FormConstants.INPUT_MAX_LENGTH_500}
        />
        <FormSelect
          label="Розтягнення"
          name="missingPerson.personal_stretching"
          control={control}
          options={[YesNoSelectOptions.YES, YesNoSelectOptions.NO]}
        />
        <FormTextInput
          label="Переломи"
          name="missingPerson.personal_fractures"
          control={control}
          placeholder="Опишіть детально"
          maxLength={FormConstants.INPUT_MAX_LENGTH_100}
        />
        <FormTextInput
          label="Родимі плями"
          name="missingPerson.personal_birthmarks"
          control={control}
          placeholder="Опишіть детально"
          maxLength={FormConstants.INPUT_MAX_LENGTH_100}
        />
        <FormTextInput
          label="Родимки"
          name="missingPerson.personal_naevus"
          control={control}
          placeholder="Опишіть детально"
          maxLength={FormConstants.INPUT_MAX_LENGTH_100}
        />
        <FormTextInput
          placeholder="Опишіть детально"
          name="missingPerson.personal_teeth_absence"
          control={control}
          label="Відсутність зубів"
          maxLength={FormConstants.INPUT_MAX_LENGTH_200}
        />
        <FormTextInput
          placeholder="Опишіть детально"
          name="missingPerson.personal_crowns_presence"
          control={control}
          label="Наявність коронок"
          maxLength={FormConstants.INPUT_MAX_LENGTH_100}
        />
        <FormTextInput
          placeholder="Опишіть детально"
          name="missingPerson.personal_implants_presence"
          control={control}
          label="Наявність імплантів"
          maxLength={FormConstants.INPUT_MAX_LENGTH_100}
        />
        <FormTextInput
          placeholder="Опишіть детально"
          name="missingPerson.personal_other_info"
          control={control}
          label="Інше"
          maxLength={FormConstants.INPUT_MAX_LENGTH_200}
        />
      </FormBlock>

      <FormBlock title="Додаткові файли">
        <DocumentsData
          withUploader
          documentTypeSelectHidden
          maxDocumentsCount={1}
          maxFileCount={10}
          maxFileSizeInMb={5}
          fileRequired={false}
          name="missingPerson.document_additional_photos"
          documentUploadLabel="Фото зниклого(Рекомендуємо додати фото в форматі 3,5*4.5)"
          request_id={currentRequestId ?? ''}
          documentTypeEntities={additionalFilesDocType ? [additionalFilesDocType] : []}
          onSaveDraft={onStepDraftSave}
          allowedFileTypes={missingPersonAllowedFileTypes}
        />
      </FormBlock>

      <StepControlPanel prev={prev} next={next} />
    </>
  );
};

export const PersonalFeaturesPreview: React.FC = () => {
  const { getValues } = useFormContext<MissingPersonFormData>();

  const personalFeaturesValues = getValues('missingPerson');

  const items: Array<[string, Nullable<string>]> = [
    ['Статура', personalFeaturesValues?.person_stature?.label],
    ['Зріст', personalFeaturesValues?.person_height],
    ['Колір волосся', personalFeaturesValues?.person_hair_color?.label],
    ['Колір очей', personalFeaturesValues?.person_eye_color?.label],
    ['Наявність залисин', personalFeaturesValues?.personal_baldness?.label],
    ['Розмір ноги', personalFeaturesValues?.personal_foot_size],
    ['Група крові', personalFeaturesValues?.personal_blood_group?.label],
    ['Шрами', personalFeaturesValues?.personal_scars],
    ['Тату', personalFeaturesValues?.personal_tattoo],
    ['Розтягнення', personalFeaturesValues?.personal_stretching?.label],
    ['Переломи', personalFeaturesValues?.personal_fractures],
    ['Родимі плями', personalFeaturesValues?.personal_birthmarks],
    ['Родимки', personalFeaturesValues?.personal_naevus],
    ['Відсутність зубів', personalFeaturesValues?.personal_teeth_absence],
    ['Наявність коронок', personalFeaturesValues?.personal_crowns_presence],
    ['Наявність імплантів', personalFeaturesValues?.personal_implants_presence],
    ['Іншe', personalFeaturesValues?.personal_other_info],
    [
      'Додайте фото, які допоможуть ідентифікувати особу (фото, на якому добре видно тату, шрами, тощо)',
      personalFeaturesValues?.document_additional_photos?.[0]?.attachments?.fileNames
        ? Object.values(personalFeaturesValues?.document_additional_photos?.[0]?.attachments?.fileNames).join(', ')
        : (personalFeaturesValues?.document_additional_photos?.[0]?.attachments?.file_name ??
          personalFeaturesValues?.document_additional_photos?.[0]?.attachments?.fileName ??
          null),
    ],
  ];

  return <Grid title="Особисті прикмети" data={items} />;
};
